/**
 * Servicio para Seguro Voluntario de Accidentes
 * 
 * Endpoints:
 * - GET /api/seguro/precio/:futbolistaId - Precio prorrateado
 * - GET /api/seguro/check/:futbolistaId - Verificar si tiene seguro
 * - POST /api/seguro/check-batch - Verificar múltiples futbolistas
 * - POST /api/seguro/comprar - Compra standalone
 */

import api from './api';

class SeguroService {

  /**
   * Obtener precio prorrateado del seguro para un futbolista
   * @param {number} futbolistaId
   * @returns {Object} { tiene_seguro, precio_base, precio_prorrateado, fecha_inicio, fecha_fin }
   */
  static async getPrecioSeguro(futbolistaId) {
    try {
      const response = await api.get(`/seguro/precio/${futbolistaId}`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error('No se pudo obtener precio del seguro');
    } catch (error) {
      console.error('❌ Error al obtener precio del seguro:', error);
      throw error;
    }
  }

  /**
   * Verificar si un futbolista tiene seguro activo
   * @param {number} futbolistaId
   * @returns {Object} { tiene_seguro, seguro }
   */
  static async checkSeguro(futbolistaId) {
    try {
      const response = await api.get(`/seguro/check/${futbolistaId}`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error('No se pudo verificar seguro');
    } catch (error) {
      console.error('❌ Error al verificar seguro:', error);
      throw error;
    }
  }

  /**
   * Verificar seguro para múltiples futbolistas (batch)
   * @param {number[]} futbolistaIds
   * @returns {Object} { futbolistaId: boolean }
   */
  static async checkSeguroBatch(futbolistaIds) {
    try {
      const response = await api.post('/seguro/check-batch', { futbolista_ids: futbolistaIds });
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error('No se pudo verificar seguros');
    } catch (error) {
      console.error('❌ Error al verificar seguros batch:', error);
      throw error;
    }
  }

  /**
   * Obtener listado de futbolistas con seguro activo (admin)
   * @returns {Object} { count, data: [...] }
   */
  static async getFutbolistasConSeguro() {
    try {
      const response = await api.get('/seguro/futbolistas');
      if (response.data.success) {
        return response.data;
      }
      throw new Error('No se pudo obtener futbolistas con seguro');
    } catch (error) {
      console.error('❌ Error al obtener futbolistas con seguro:', error);
      throw error;
    }
  }

  /**
   * Comprar seguro de forma standalone (sin paquete)
   * @param {number} futbolistaId
   * @param {string} metodoPago - 'redsys', 'transferencia', etc.
   * @returns {Object} { transaction_id, transactionId, amount, ... }
   */
  static async comprarSeguro(futbolistaId, metodoPago = 'redsys') {
    try {
      const response = await api.post('/seguro/comprar', {
        futbolista_id: futbolistaId,
        metodo_pago: metodoPago
      });
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Error al comprar seguro');
    } catch (error) {
      console.error('❌ Error al comprar seguro:', error);
      throw error;
    }
  }
}

export default SeguroService;
